/**
 * Global Search Presenter
 *
 * Platform-agnostic derivation of presentation data from the
 * `GlobalSearchResult` discriminated union: navigation href, display title,
 * optional subtitle, stable row id, default category, plus the canonical
 * per-type group label and render order.
 *
 * Web (`useGlobalSearch`) and mobile (`global-search.map`) both render the same
 * results, so this logic lives here once. Anything platform-specific — icons
 * (heroicon components vs RN icon names), persistence, navigation — stays in the
 * respective app.
 */

import type { GlobalSearchResult } from '../types/domain/search.types';

/** A single live-result type (e.g. `'plan'`, `'workout'`). */
export type GlobalSearchResultType = GlobalSearchResult['type'];

/** Section header label per result type. */
export const GLOBAL_SEARCH_TYPE_LABEL: Record<GlobalSearchResultType, string> =
  {
    plan: 'Plans',
    workout: 'Workouts',
    exercise: 'Exercises',
    challenge: 'Challenges',
    friend: 'People',
    client: 'Clients',
  };

/** Stable order the result groups are rendered in. */
export const GLOBAL_SEARCH_TYPE_ORDER: readonly GlobalSearchResultType[] = [
  'plan',
  'workout',
  'exercise',
  'challenge',
  'friend',
  'client',
];

/**
 * Navigation path for a result.
 *
 * Two things were wrong here and both had to change for these links to work.
 *
 * Detail routes are SINGULAR (`/plan/:id`); only list routes are plural
 * (`/plans`). Every type except `friend` was emitting the plural form.
 *
 * They are also keyed by `:id` — a hashid string — not by slug. Plan and
 * workout results carry both, and were handing over the slug, so correcting
 * the pluralisation alone would still have resolved to nothing.
 *
 * These paths duplicate the web route table, which is what let them drift —
 * `packages/web/src/utils/routes.ts` cannot be imported from shared. A guard
 * test in the web package asserts the two agree; see
 * `web/src/utils/__tests__/global-search-routes.test.ts`.
 */
export function getGlobalSearchResultHref(result: GlobalSearchResult): string {
  switch (result.type) {
    case 'plan':
      return `/plan/${result.id}`;
    case 'workout':
      return `/workout/${result.id}`;
    case 'exercise':
      return `/exercise/${result.id}`;
    case 'challenge':
      return `/challenge/${result.id}`;
    case 'friend':
      return `/profile/${result.username}`;
    // Keyed on the handle, not the hashid — the client view route is
    // `/client/:username`.
    case 'client':
      return `/client/${result.username}`;
  }
}

/** Display title for a result. */
export function getGlobalSearchResultTitle(result: GlobalSearchResult): string {
  switch (result.type) {
    case 'plan':
    case 'workout':
    case 'exercise':
    case 'client':
      return result.name;
    case 'challenge':
      return result.title;
    case 'friend':
      return result.username;
  }
}

/** Optional subtitle for a result. */
export function getGlobalSearchResultSubtitle(
  result: GlobalSearchResult
): string | undefined {
  switch (result.type) {
    case 'exercise':
      return `Target: ${result.target}`;
    default:
      return undefined;
  }
}

/**
 * Default command category a live result belongs to when flattened alongside
 * Pages / Actions rows (web grouping).
 */
export function getGlobalSearchResultCategory(
  result: GlobalSearchResult
): 'pages' | 'actions' {
  switch (result.type) {
    case 'friend':
    case 'client':
      return 'actions';
    default:
      return 'pages';
  }
}

/** Stable id (`type-key`) for list keys + recents dedupe. */
export function getGlobalSearchResultId(result: GlobalSearchResult): string {
  const key = 'id' in result ? result.id : result.username;
  return `${result.type}-${key}`;
}
